import { cellsOf, EXIT_ROW, SIZE, type Block, type Level } from './level';

/** 슬라이딩 블록 판. 좌표는 칸 단위(실수 허용 — 드래그 중 표시용). */
export class Board {
  blocks: Block[];
  moves = 0;
  /** 드래그 중인 블록의 실수 오프셋(칸). 표시용. */
  drag: { i: number; offset: number; start: number } | null = null;
  private done = false;

  constructor(level: Level) {
    this.blocks = level.blocks.map((b) => ({ ...b }));
  }

  occupancy(except = -1): number[][] {
    const g: number[][] = Array.from({ length: SIZE }, () => Array(SIZE).fill(-1));
    this.blocks.forEach((b, i) => { if (i !== except) for (const [r, c] of cellsOf(b)) g[r]![c] = i; });
    return g;
  }

  /** 그 방향(sign ±1)으로 갈 수 있는 최대 칸 수. */
  maxSlide(i: number, sign: 1 | -1): number {
    const b = this.blocks[i]!;
    const g = this.occupancy(i);
    let n = 0;
    while (true) {
      const k = n + 1;
      const cells = cellsOf({ ...b, r: b.dir === 'v' ? b.r + sign * k : b.r, c: b.dir === 'h' ? b.c + sign * k : b.c });
      if (cells.some(([r, c]) => r < 0 || c < 0 || r >= SIZE || c >= SIZE || g[r]![c] !== -1)) return n;
      n = k;
    }
  }

  blockAt(r: number, c: number): number {
    if (r < 0 || c < 0 || r >= SIZE || c >= SIZE) return -1;
    return this.occupancy()[r]![c]!;
  }

  /** 드래그 시작(칸 좌표, 실수). 잡은 블록 index 또는 null. */
  beginDrag(x: number, y: number): number | null {
    if (this.done) return null;
    const i = this.blockAt(Math.floor(y), Math.floor(x));
    if (i < 0) return null;
    const b = this.blocks[i]!;
    this.drag = { i, offset: 0, start: b.dir === 'h' ? x : y };
    return i;
  }

  /** 드래그 이동: 축 성분만, 갈 수 있는 범위로 클램프. */
  dragTo(x: number, y: number): void {
    if (!this.drag) return;
    const b = this.blocks[this.drag.i]!;
    const want = (b.dir === 'h' ? x : y) - this.drag.start;
    const lo = -this.maxSlide(this.drag.i, -1), hi = this.maxSlide(this.drag.i, 1);
    this.drag.offset = Math.max(lo, Math.min(hi, want));
  }

  /** 손 뗌: 반올림해 스냅. 바뀌었으면 moves+1. */
  endDrag(): void {
    if (!this.drag) return;
    const b = this.blocks[this.drag.i]!;
    const d = Math.round(this.drag.offset);
    if (d !== 0) {
      if (b.dir === 'h') b.c += d; else b.r += d;
      this.moves++;
    }
    this.drag = null;
    if (this.cleared()) this.done = true;
  }

  cleared(): boolean {
    const red = this.blocks.find((b) => b.red)!;
    return red.r === EXIT_ROW && red.c + red.len - 1 === SIZE - 1;
  }
}
